import React, { FC, useState, useEffect } from 'react'
import i18n from 'i18next'
import { IonPopover } from '@ionic/react'
import { observer } from 'mobx-react'
import { money } from './util'
import payMobx, { ITipOption } from '@mobx/pay/pay.mobx'
import AmountKeyboard from './AmountKeyboard'

interface IProps {
  tip: number;
  tipSelected?: boolean;
  onTipChange?(tip: number): void;
}

const TipSelect: FC<IProps> = ({ tip, tipSelected, onTipChange }) => {
  const [tipPopoverEvent, setTipPopoverEvent] = useState(null)
  const [showCustomTip, setShowCustomTip] = useState(false)

  let selectedTipOption
  if (tipSelected) {
    selectedTipOption = payMobx.tipOptions.find(x => x.amount === tip)
    if (!selectedTipOption) {
      selectedTipOption = payMobx.tipOptions.find(x => x.customTip)
    }
  }

  const setTip = (tip: number) => {
    setShowCustomTip(false)
    setTipPopoverEvent(null)
    if (!isNaN(tip)) {
      onTipChange && onTipChange(tip)
    }
  }

  function percentage (ratio: number) {
    return (Math.round(ratio * 10000) / 100) + '%'
  }

  const renderTipOption = (option: ITipOption) => {
    let key: string | number
    const classNames = []
    if (selectedTipOption === option) {
      classNames.push('selected')
    }
    let onClick: (event: Event) => void
    const children = []

    if (option.customTip) {
      key = 'custom-tip'
      classNames.push('custom-tip')
      children.push(selectedTipOption === option && tipSelected ? money(tip) : i18n.t('pay.customTip'))
      onClick = () => {
        setTipPopoverEvent(null)
        setShowCustomTip(true)
      }
    } else if (option.noTip) {
      key = 'no-tip'
      classNames.push('no-tip')
      onClick = () => setTip(option.amount)
      children.push(<span>{i18n.t('pay.noTip')}</span>)
    } else {
      key = option.amount
      onClick = () => setTip(option.amount)
      children.push(<span>{money(option.amount)}</span>)
      if (option.ratio) {
        children.push(<span className='hint'>{percentage(option.ratio)}</span>)
      }
    }

    const className = classNames.join(' ')

    return React.createElement(
      'li',
      { key, className, onClick },
      ...children
    )
  }

  return (
    <>
      <IonPopover
        isOpen={tipPopoverEvent != null}
        event={tipPopoverEvent}
        showBackdrop={false}
        cssClass='tip-select-popover'
        onDidDismiss={() => setTipPopoverEvent(null)}
      >
        <ul>
          {payMobx.tipOptions.map(x => renderTipOption(x))}
        </ul>
      </IonPopover>
      <div className='value' onClick={(e) => setTipPopoverEvent(e.nativeEvent)}>
        {selectedTipOption ? (
          <>
            {selectedTipOption.noTip ? (
              <span>{i18n.t('pay.noTip')}</span>
            ) : (
              <>
                <span>{money(tip)}</span>
                {selectedTipOption.ratio && <span>{percentage(selectedTipOption.ratio)}</span>}
              </>
            )}
          </>
        ) : (
          <span>{i18n.t('pay.selectTip')}</span>
        )}
      </div>
      <AmountKeyboard
        visible={showCustomTip}
        amount={tip}
        max={9999.99}
        onClose={() => setShowCustomTip(false)}
        onSave={x => setTip(x)}
      />
    </>
  )
}

export default observer(TipSelect)
